import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import type { Aspect, Design } from '@noorview/core';
import { ASPECTS, clampRect, keepShape, minSize, normalizeDesign } from '@noorview/core';
import { attempt, backend, screenStatus, toast, useApp } from '../data/store';
import { Icon } from '../ui/icons';
import { IconButton, Popover, Segmented, choose } from '../ui/controls';
import { openSheet, useMedia, useStudioPanels, useNav } from '../app/ui';
import { CANVAS_NAME, blankCanvas, canvasOf, designOf } from '../app/designs';
import { Boundary } from '../app/Errors';
import { copySelected, pasteHere } from './clipActions';
import { setPicking } from './clipboard';
import { useStudio, type SaveState } from './store';
import { duplicateWidget, switchAspect } from './ops';
import { openIssues } from './Contrast';
import { Library } from './Library';
import { Drawer } from './Drawer';
import { StudioLock, useLocked } from '../app/lock';
import { Canvas } from './Canvas';
import { Inspector, LiveOnScreen } from './Inspector';
import { TemplatesCard, saveCurrent } from './Templates';
import { brandPromptDue } from '../sheets/Logo';

export function StudioPage() {
  const { screenId } = useParams();
  const ws = useApp((s) => s.ws)!;
  const nav = useNav();
  const screen = ws.screens.find((s) => s.id === screenId) ?? null;
  // From the menu (no screen): the Studio's own blank canvas, made the first time it's needed.
  const canvas = !screenId ? canvasOf(ws) : null;
  const rec = screenId ? (screen ? designOf(ws, screen.id) : null) : canvas;
  const making = useRef(false);
  useEffect(() => {
    if (screenId || canvas || making.current) return;
    making.current = true;
    void attempt(() => backend.createDesign(CANVAS_NAME, blankCanvas())).then(() => useApp.getState().reload()).finally(() => { making.current = false; });
  }, [screenId, canvas]);
  const open = useStudio((s) => s.open);
  const designId = useStudio((s) => s.designId);
  const revision = useStudio((s) => s.revision);
  const save = useStudio((s) => s.save);
  const design = useStudio((s) => s.design);

  // Open the screen's design; switching screens saves the current one first.
  useEffect(() => {
    if (!rec) return;
    if (designId !== rec.id) {
      const st = useStudio.getState();
      void (st.designId ? st.flush() : Promise.resolve(true)).then(() => open(rec));
    }
  }, [rec?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Edit in Studio" on a screen (Screens, the dashboard) starts from what that screen is showing now. Unsent changes
  // aren't lost: switching to the live version is one change, so Undo brings them straight back.
  const replaceUrl = useNavigate();
  const startLive = useRef(new URLSearchParams(location.search).get('start') === 'live');
  useEffect(() => {
    if (!startLive.current || !rec || designId !== rec.id || !design) return;
    startLive.current = false;
    replaceUrl(location.pathname, { replace: true });
    if (!rec.published || JSON.stringify(normalizeDesign(rec.published)) === JSON.stringify(design)) return;
    const live = normalizeDesign(rec.published);
    useStudio.getState().change(() => live);
    toast(`Showing what’s on ${screen?.name ?? 'the screen'} now`, { action: { label: 'Undo', run: () => useStudio.getState().undo() } });
  }, [rec, designId, design]); // eslint-disable-line react-hooks/exhaustive-deps

  // Someone else saved this design (another tab or editor): take their version if we have nothing unsaved.
  useEffect(() => {
    if (rec && rec.id === designId && rec.draftRevision > revision && save === 'saved') {
      const sel = useStudio.getState().selected;
      open(rec);
      if (sel) useStudio.getState().select(sel);
    }
  }, [rec, designId, revision, save, open]);

  // Warn before closing with unsaved work.
  useEffect(() => {
    const onUnload = (e: BeforeUnloadEvent) => { const s = useStudio.getState().save; if (s === 'unsaved' || s === 'saving' || s === 'error') { void useStudio.getState().flush(); e.preventDefault(); } };
    window.addEventListener('beforeunload', onUnload);
    return () => { window.removeEventListener('beforeunload', onUnload); void useStudio.getState().flush(); };
  }, []);

  useStudioShortcuts(screen?.name ?? 'this screen');
  const systemLocked = useLocked();
  // Lock means lockdown: the design of a locked screen can't be touched until that screen is unlocked.
  const held = ws.screens.filter((s) => s.locked && rec && s.designId === rec.id);
  const locked = systemLocked || held.length > 0;

  // Smaller windows: the library (and on narrow windows the inspector too) slide in as drawers over the canvas.
  const libDrawer = useMedia('(max-width: 1320px)');
  const inspDrawer = useMedia('(max-width: 1040px)');
  const short = useMedia('(max-height: 760px)');
  const panels = useStudioPanels();
  const selected = useStudio((s) => s.selected);
  useEffect(() => { if (inspDrawer && selected) useStudioPanels.getState().set({ inspector: true, library: false }); }, [selected, inspDrawer]);
  useEffect(() => { if (!libDrawer) useStudioPanels.getState().set({ library: false }); if (!inspDrawer) useStudioPanels.getState().set({ inspector: false }); }, [libDrawer, inspDrawer]);

  // Before the first design session, ask for the masjid's logo (once; "Skip for now" is remembered).
  const canEditMasjid = useApp((s) => s.session?.role !== 'editor');
  useEffect(() => {
    if (canEditMasjid && brandPromptDue(ws.masjid) && !locked) openSheet({ name: 'logo', first: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (screenId && (!screen || !rec)) return <div className="empty" style={{ flex: 1 }}><b>Screen not found</b><button type="button" className="btn primary" onClick={() => nav('/')}>Back to the dashboard</button></div>;
  if (!rec) return <div className="loading-page"><div className="skeleton" style={{ height: 56 }} /><div className="skeleton" style={{ flex: 1 }} /></div>;
  if (!design || designId !== rec.id) return <div className="loading-page"><div className="skeleton" style={{ height: 56 }} /><div className="skeleton" style={{ flex: 1 }} /></div>;

  return (
    <div className="studio-wrap">
    {locked && <StudioLock screens={systemLocked ? undefined : held} />}
    <div className={`studio${screen ? '' : ' is-canvas'}`} inert={locked || undefined} aria-hidden={locked || undefined}>
      <Toolbar screenId={screen?.id} />
      <ConflictBanner />
      <div className={`workspace${libDrawer ? ' lib-drawer' : ''}${inspDrawer ? ' insp-drawer' : ''}`}>
        <Drawer side="left" enabled={libDrawer} open={panels.library} onClose={() => panels.set({ library: false })} label="Widgets and layers">
          <Boundary label="Widget library"><Library /></Boundary>
        </Drawer>
        <div className="center">
          <Boundary label="Canvas"><Canvas /></Boundary>
          <Boundary label="Templates"><TemplatesCard /></Boundary>
        </div>
        <Drawer side="right" enabled={inspDrawer} open={panels.inspector} onClose={() => panels.set({ inspector: false })} label="Inspector">
          <div className="right">
            <Boundary label="Inspector"><Inspector /></Boundary>
            {!short && <Boundary label="Live preview"><LiveOnScreen screenId={screen?.id} /></Boundary>}
          </div>
        </Drawer>
      </div>
    </div>
    </div>
  );
}

/* ---------- toolbar ---------- */

function Toolbar({ screenId }: { screenId?: string }) {
  const ws = useApp((s) => s.ws)!;
  const now = useApp((s) => s.now);
  const nav = useNav();
  const st = useStudio();
  // No screen: the Studio's blank canvas (Send Live asks which screen to send it to).
  const screen = screenId ? ws.screens.find((s) => s.id === screenId) ?? null : null;
  const rec = screen ? designOf(ws, screen.id)! : canvasOf(ws)!;
  const pickRef = useRef<HTMLButtonElement>(null);
  const [pick, setPick] = useState(false);
  const [sending, setSending] = useState(false);
  const d = st.design!;
  const size = ASPECTS[d.aspect];
  const unchanged = useMemo(() => !!screen && !!rec.published && JSON.stringify(rec.published) === JSON.stringify(d), [screen, rec.published, d]);

  /** Send Live. `ask`: confirm first (Exit has already asked). True once it's sent. */
  const sendLive = async (ask = true): Promise<boolean> => {
    if (!screen) return sendCanvas();
    if (sending) return false;
    setSending(true);
    try {
      const status = screenStatus(screen, Date.now());
      if (ask) {
        const issues = openIssues().filter((i) => i.severity === 'problem');
        const warn = issues.length ? ` ${issues.length === 1 ? issues[0]!.message : `${issues.length} widgets may be hard to read on this background.`}` : '';
        const pick = await choose('Send edits live?', {
          body: `${status === 'unpaired' || status === 'offline' ? `${screen.name} will update as soon as its display is online.` : `${screen.name} will update straight away.`} You can restore a saved version at any time.${warn}`,
          choices: [{ value: 'later', label: 'Not yet', kind: 'plain' }, { value: 'send', label: 'Send Live', kind: 'primary' }],
        });
        if (pick !== 'send') return false;
      }
      const ok = await st.flush();
      if (!ok) { toast('Save your changes first (see the message above).', { bad: true }); return false; }
      const r = await attempt(() => backend.publish(rec.id));
      if (!r) return false;
      toast(status === 'online' || status === 'idle' || status === 'off' ? `Sent live to ${screen.name}` : `Published. ${screen.name} will update as soon as its display is online.`);
      watchDelivery(screen.id, r.version, Date.now());
      return true;
    } finally { setSending(false); }
  };

  /** The blank canvas to a screen: it replaces what that screen shows (laid out for its shape) and goes live there. */
  const sendCanvas = async (): Promise<boolean> => {
    if (sending) return false;
    if (!ws.screens.length) { toast('Add a screen first, then send this to it.'); openSheet({ name: 'addScreen' }); return false; }
    const pick = await choose('Send to which screen?', {
      body: 'The canvas replaces what that screen shows now, including any changes on it that haven’t been sent live. The canvas stays here as it is.',
      choices: [{ value: '', label: 'Cancel', kind: 'plain' }, ...ws.screens.map((s) => ({ value: s.id, label: s.name, kind: 'primary' as const }))],
    });
    const target = ws.screens.find((s) => s.id === pick);
    const trec = target ? designOf(ws, target.id) : null;
    if (!target || !trec) return false;
    setSending(true);
    try {
      if (!(await st.flush())) { toast('Save your changes first (see the message above).', { bad: true }); return false; }
      const cur = useStudio.getState().design!;
      const aspect = (trec.published ?? trec.draft).aspect;
      const next = cur.aspect === aspect ? cur : switchAspect(cur, aspect);
      if (!(await attempt(() => backend.saveDraft(trec.id, structuredClone(next), trec.draftRevision, true)))) return false;
      const r = await attempt(() => backend.publish(trec.id));
      if (!r) return false;
      const status = screenStatus(target, Date.now());
      toast(status === 'online' || status === 'idle' || status === 'off' ? `Sent live to ${target.name}` : `Published. ${target.name} will update as soon as its display is online.`);
      watchDelivery(target.id, r.version, Date.now());
      return true;
    } finally { setSending(false); }
  };

  /**
   * Exit (the red button). Every change saves itself, so this saves anything still pending, then says whether the
   * edits are live yet, with Send Live and Save as a template on the way out. Same as the iPhone app.
   */
  const [exiting, setExiting] = useState(false);
  const exit = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      const saved = await st.flush();
      if (!saved) {
        const conflict = useStudio.getState().save === 'conflict';
        const pick = await choose('Your latest changes aren’t saved yet', {
          body: conflict ? 'Someone else changed this screen while you were editing. Choose which version to keep (see the message above the canvas), then exit.' : 'NoorView couldn’t save your last few changes, so they could be lost if you exit now. Check your internet connection and try again.',
          choices: [{ value: 'exit', label: 'Exit anyway', kind: 'danger' }, { value: 'stay', label: 'Keep editing', kind: 'primary' }],
        });
        if (pick === 'exit') nav('/');
        return;
      }
      // The blank canvas keeps what's on it until Clear: nothing to ask on the way out.
      if (!screen) { nav('/'); return; }
      const cur = useStudio.getState().design;
      const live = !!rec.published && JSON.stringify(rec.published) === JSON.stringify(cur);
      if (live) {
        const pick = await choose('Exit the Studio?', {
          body: `Your changes are saved, and ${screen.name} is showing them. You can come back and edit at any time.`,
          choices: [{ value: 'stay', label: 'Keep editing', kind: 'plain' }, { value: 'exit', label: 'Exit', kind: 'danger' }],
        });
        if (pick === 'exit') nav('/');
        return;
      }
      const pick = await choose('Exit the Studio?', {
        body: <>
          <p>Your changes have been saved automatically, but they haven’t been sent live, so {screen.name} isn’t showing them yet. You can come back to the Studio and carry on editing at any time.</p>
          <p>To put them on the screen, click Send Live{screen.deviceId ? '' : ', then pair a display'}. You can also save this layout as a template before you go.</p>
        </>,
        choices: [
          { value: 'stay', label: 'Keep editing', kind: 'plain' },
          { value: 'template', label: 'Save as a template', kind: 'plain' },
          { value: 'exit', label: 'Exit', kind: 'danger' },
          { value: 'send', label: 'Send Live', kind: 'primary' },
        ],
      });
      if (pick === 'exit') { nav('/'); return; }
      if (pick === 'template') { if (await saveCurrent()) nav('/'); return; }
      if (pick !== 'send' || !(await sendLive(false))) return;
      nav('/');
      // Sent, but nothing to show it on yet: straight on to pairing.
      if (!screen.deviceId) setTimeout(() => openSheet({ name: 'pair', screenId: screen.id }), 300);
    } finally { setExiting(false); }
  };

  return (
    <div className="toolbar">
      <button type="button" className="btn danger exit" onClick={() => void exit()} disabled={exiting} title="Exit the Studio. Your changes are saved automatically.">
        <Icon name="logout" size={18} /><span className="lbl">Exit</span>
      </button>
      <button ref={pickRef} type="button" className="screen-pick" onClick={() => setPick((v) => !v)} aria-haspopup="menu" aria-expanded={pick}>
        <span>{screen?.name ?? 'Blank canvas'}</span><Icon name="chevronDown" size={20} />
      </button>
      <Popover anchor={pickRef.current} open={pick} onClose={() => setPick(false)} width={280} align="start" label="Choose a screen">
        <div className="menu">
          <button type="button" onClick={() => { setPick(false); nav('/studio'); }} aria-current={!screenId}>
            <Icon name={!screenId ? 'check' : 'grid'} size={18} />Blank canvas
          </button>
          {ws.screens.map((s) => (
            <button key={s.id} type="button" onClick={() => { setPick(false); nav(`/studio/${s.id}?start=live`); }} aria-current={s.id === screenId}>
              <Icon name={s.id === screenId ? 'check' : 'monitor'} size={18} />{s.name}
            </button>
          ))}
          <button type="button" onClick={() => { setPick(false); openSheet({ name: 'addScreen' }); }}><Icon name="plus" size={18} />Add screen</button>
        </div>
      </Popover>
      <SaveStatus state={st.save} at={st.savedAt} now={now} onRetry={() => void st.flush()} />
      <div className="spacer" />
      <div className="fmt">
        <Segmented label="Display format" fixed value={d.aspect} onChange={(a: Aspect) => st.change((x) => switchAspect(x, a))}
          options={(['16:9', '9:16', '21:9', '4:3'] as Aspect[]).map((a) => ({ value: a, label: a }))} />
        <span className="hint mono">{size.width} × {size.height}</span>
      </div>
      <div className="spacer" />
      <IconButton icon="undo" label="Undo (⌘Z)" disabled={!st.past.length} onClick={st.undo} />
      <IconButton icon="redo" label="Redo (⇧⌘Z)" disabled={!st.future.length} onClick={st.redo} />
      <button type="button" className="btn secondary" onClick={() => openSheet({ name: 'fullscreen', screenId: screenId ?? '', design: d, label: 'Preview' })} title="Preview"><Icon name="eye" size={20} /><span className="lbl">Preview</span></button>
      <button type="button" className="btn primary" onClick={() => (unchanged ? toast(`${screen!.name} is already showing this version`) : void sendLive())} disabled={sending} title={unchanged ? 'The screen is already showing this' : 'Send your changes to the screen'}>
        <Icon name="send" size={18} />{sending ? 'Sending…' : 'Send Live'}
      </button>
    </div>
  );
}

/** After publishing, confirms when the screen reports it's showing the new version. */
function watchDelivery(screenId: string, version: number, started: number) {
  const unsub = useApp.subscribe((s) => {
    const sc = s.ws?.screens.find((x) => x.id === screenId);
    if (sc?.showingVersion != null && sc.showingVersion >= version) {
      unsub();
      const secs = ((Date.now() - started) / 1000).toFixed(1);
      toast(`${sc.name} is now showing your changes (${secs}s)`);
    }
  });
  setTimeout(unsub, 120_000);
}

function SaveStatus({ state, at, now, onRetry }: { state: SaveState; at: string | null; now: number; onRetry: () => void }) {
  const mins = at ? Math.max(0, Math.round((now - Date.parse(at)) / 60_000)) : 0;
  const text = state === 'saving' ? 'Saving…' : state === 'unsaved' ? 'Unsaved changes' : state === 'error' ? 'Couldn’t save' : state === 'conflict' ? 'Edited elsewhere' : `Saved · ${mins < 1 ? 'just now' : `${mins}m ago`}`;
  return (
    <div className={`save-status glass ${state}`} role="status" aria-live="polite">
      <Icon name={state === 'error' || state === 'conflict' ? 'alert' : state === 'saved' ? 'check' : 'refresh'} size={16} className={state === 'saving' ? 'spin' : ''} />
      <span>{text}</span>
      {state === 'error' && <button type="button" className="link" onClick={onRetry}>Retry</button>}
    </div>
  );
}

function ConflictBanner() {
  const conflict = useStudio((s) => s.conflict);
  const save = useStudio((s) => s.save);
  const resolve = useStudio((s) => s.resolveConflict);
  if (save !== 'conflict' || !conflict) return null;
  return (
    <div className="banner" role="alert">
      <Icon name="alert" size={20} />
      <span style={{ flex: 1 }}>Someone else changed this screen while you were editing. Choose which version to keep.</span>
      <button type="button" className="btn sm plain" onClick={() => resolve('theirs')}>Use their version</button>
      <button type="button" className="btn sm primary" onClick={() => resolve('mine')}>Keep mine</button>
    </div>
  );
}

/* ---------- keyboard ---------- */

function useStudioShortcuts(screenName: string) {
  const nameRef = useRef(screenName);
  nameRef.current = screenName;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('input,textarea,select,[contenteditable="true"]') || document.querySelector('.scrim, .studio-lock')) return;
      const st = useStudio.getState();
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) st.redo(); else st.undo(); return; }
      if (mod && e.key.toLowerCase() === 'y') { e.preventDefault(); st.redo(); return; }
      const d = st.design, w = d?.widgets.find((x) => x.id === st.selected);
      if (mod && !e.shiftKey && e.key.toLowerCase() === 'c' && !window.getSelection()?.toString()) { e.preventDefault(); copySelected(nameRef.current); return; }
      if (mod && !e.shiftKey && e.key.toLowerCase() === 'v') { e.preventDefault(); pasteHere(nameRef.current); return; }
      if (e.key === 'Escape') { st.select(null); setPicking(false); return; }
      if (!d || !w) return;
      if (mod && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        const copy = duplicateWidget(d, w);
        st.change((x) => ({ ...x, widgets: [...x.widgets, copy] }));
        st.select(copy.id);
        return;
      }
      if (w.locked) return;
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); st.change((x) => ({ ...x, widgets: x.widgets.filter((y) => y.id !== w.id) })); st.select(null); return; }
      const dir: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
      const mv = dir[e.key];
      if (!mv) return;
      e.preventDefault();
      const [dx, dy] = mv;
      const min = minSize(w, d.cols, d.rows);
      const r = e.shiftKey ? keepShape(w, clampRect({ ...w, w: w.w + dx, h: w.h + dy }, d.cols, d.rows, min), d, dx ? 'w' : 'h') : clampRect({ ...w, x: w.x + dx, y: w.y + dy }, d.cols, d.rows, min);
      st.updateWidget(w.id, r, e.shiftKey ? 'kresize' : 'kmove');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

export type { Design };
